import { Autocomplete, Grid, Popper, PopperProps, TextField, Typography } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { ADDRESSES_LABELS, COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from './CountryCodes';
import { useTranslation } from 'react-i18next';
import { useDataValidations } from '../../../shared/hooks/useDataValidations';
import CustomArrowIcon from '../../CustomArrowIcon/CustomArrowIcon';
type CountryCode = {
  code: string;
  dial_code: string;
  emoji: string;
  name: string;
};

interface PhoneProps {
  onChange: (value: string) => void;
  city?: { name: string };
  telephone?: string;
  disabled?: boolean;
  checkLoadingData?: () => React.ReactNode;
  isLoadingUserData: boolean;
}

const CustomPopper = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ minWidth: '300px', maxWidth: '400px' }}
    placement="bottom-start"
  />
);

const Phonefield = ({ onChange, telephone, disabled, checkLoadingData, isLoadingUserData }: PhoneProps) => {
  const { t } = useTranslation();
  const areaCodeRef = useRef<HTMLInputElement>(null!);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [areaCode, setAreaCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const fullPhoneNumber = `${countryCode.dial_code} ${areaCode} ${phoneNumber}`;
  const { isPhoneError } = useDataValidations();
  const error = isPhoneError(fullPhoneNumber);
  const [localDisabled, setLocalDisabled] = useState(disabled);


  const [areaCodeError, setAreaCodeError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);

  const getCountryCode = (telephone: string | undefined) => {
    if (telephone === undefined || telephone === null || telephone?.length === 0 || !telephone.includes(' ')) {
      return DEFAULT_COUNTRY_CODE;
    }
    const value = telephone.split(' ')[0];
    return COUNTRY_CODES.find(code => code.dial_code === value) ?? DEFAULT_COUNTRY_CODE;
  }

  const getAreaCode = (telephone: string | undefined) => {
    if (telephone === undefined || telephone === null || telephone?.length === 0 || !telephone.includes('-')) {
      return '';
    }
    let value = telephone.split('-')[0];
    if (telephone.includes(' ')) {
      value = value.split(' ')[1];
    }
    return value;
  }

  const getPhoneNumber = (telephone: string | undefined) => {
    if (telephone === undefined || telephone === null || telephone?.length === 0 || !telephone.includes('-')) {
      return '';
    }
    let value = telephone.split('-')[1];
    return value;
  }

  useEffect(() => {
    if (!isLoadingUserData) {
      if (telephone && telephone !== '') {
        const parsedCountry = getCountryCode(telephone);
        const parsedArea = getAreaCode(telephone);
        const parsedNumber = getPhoneNumber(telephone);

        const unformatted = !telephone.includes(' ') || !telephone.includes('-');

        setCountryCode(parsedCountry);
        setAreaCode(parsedArea);
        setPhoneNumber(parsedNumber);

        if (unformatted || (parsedArea === '' && parsedNumber === '')) {
          setLocalDisabled(false);
        }
      } else {
        setLocalDisabled(false);
      }
    }
  }, [telephone, disabled, isLoadingUserData]);


  const validatePhoneErrors = (_areaCode: string, _phoneNumber: string): [boolean, boolean, boolean] => {
    const areaCodeLength = _areaCode?.length ?? 0;
    const phoneNumberLength = _phoneNumber?.length ?? 0;
    const totalChar = areaCodeLength + phoneNumberLength;

    const hasAreaCodeErrors = areaCodeLength > 0 && phoneNumberLength > 0 && (totalChar !== 10 || _areaCode?.startsWith('0'));
    const hasPhoneNumberErrors = phoneNumberLength > 0 && (totalChar !== 10 || _phoneNumber?.startsWith('15'));
    setAreaCodeError(hasAreaCodeErrors);
    setPhoneNumberError(hasPhoneNumberErrors);
    return [hasAreaCodeErrors, hasPhoneNumberErrors, totalChar !== 10]
  }

  const changePhone = (_countryCode: CountryCode, _areaCode: string, _phoneNumber: string) => {
    const [hasAreaErrors, hasPhoneErrors, invalidLength] = validatePhoneErrors(_areaCode, _phoneNumber)
    if (!hasAreaErrors && !hasPhoneErrors && !invalidLength) {
      const number = `${_countryCode.dial_code} ${_areaCode}-${_phoneNumber}`
      onChange(number)
    } else {
      onChange('')
    }
  }

  const handleOnPhoneChange = (type: 'countryCode' | 'areaCode' | 'phoneNumber', value: any) => {
    switch (type) {
      case 'countryCode':
        setCountryCode(value);
        changePhone(value, areaCode, phoneNumber);
        break;
      case 'areaCode':
        setAreaCode(value);
        changePhone(countryCode, value, phoneNumber);
        break;
      default:
        setPhoneNumber(value);
        changePhone(countryCode, areaCode, value);
        break;
    }
  }

  const handlePhoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && (event.target as HTMLInputElement).value === '') {
      areaCodeRef.current?.focus();
    }
  };

  const phoneHelper = () => {
    if (areaCodeError) {
      return telephone && error && t('invalidAreaCode');
    }
    if (phoneNumberError) {
      return telephone && error && t('invalidPhoneAR');
    }
    return '';
  };


  return (
    <Grid container spacing={3}>
      <Grid item md={4} sm={4} xs={12} >
        <Autocomplete
          popupIcon={<CustomArrowIcon testId="country-code-select-arrow" />}
          disabled={localDisabled}
          tabIndex={-1}
          style={{ width: '100%' }}
          disableClearable
          value={countryCode}
          options={COUNTRY_CODES}
          onChange={(_e, value) => handleOnPhoneChange('countryCode', value)}
          getOptionLabel={(option) => `${option.emoji} ${option.dial_code} - ${option.name}`}
          PopperComponent={CustomPopper}
          renderInput={(params) =>
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, tabIndex: 13, 'data-testid': 'country-code-input' }}
              size='small'
              label={<>{t('countryPhoneCode')}</>}
              autoComplete='off'
              tabIndex={-1}
            />
          }
        />
      </Grid>
      <Grid item md={8} sm={8} xs={12}>
        <Grid container spacing={1}>
          <Grid item md={4} sm={4} xs={4}>
            <TextField
              disabled={localDisabled}
              style={{ width: '100%' }}
              error={areaCode !== "" && areaCodeError}
              helperText={phoneHelper()}
              value={areaCode}
              size='small'
              inputRef={areaCodeRef}
              onChange={(e) => handleOnPhoneChange('areaCode', e.target.value)}
              name="telephone"
              tabIndex={-1}
              id="areaCode-input"
              label={ADDRESSES_LABELS.areaCode}
              autoComplete='off'
              inputProps={{
                'data-testid': 'area-code-input',
                maxLength: 4,
                onKeyPress: (event) => {
                  if (!/\d/.test(event.key)) {
                    event.preventDefault();
                  }
                },
                tabIndex: 14
              }}
              InputProps={{
                endAdornment: checkLoadingData?.()
              }}
            />
          </Grid>
          <Grid item md={8} sm={8} xs={8}>
            <TextField
              disabled={localDisabled}
              style={{ width: '100%' }}
              error={phoneNumber !== "" && phoneNumberError}
              helperText={phoneHelper()}
              value={phoneNumber}
              size='small'
              onKeyDown={handlePhoneKeyDown}
              onChange={(e) => handleOnPhoneChange('phoneNumber', e.target.value)}
              name="telephone"
              tabIndex={-1}
              id="phone-input"
              label={ADDRESSES_LABELS.cellphone}
              autoComplete='off'
              inputProps={{
                'data-testid': 'phone-input',
                maxLength: 8,
                onKeyPress: (event) => {
                  if (!/\d/.test(event.key)) {
                    event.preventDefault();
                  }
                },
                tabIndex: 15
              }}
              InputProps={{
                endAdornment: checkLoadingData?.()
              }}
            />
          </Grid>
        </Grid>

        <Typography color="#949494" sx={{ fontSize: '13px' }}>
          {t('invalidPhoneAR')}
        </Typography>

      </Grid>
    </Grid>
  );
};

export default Phonefield;
